// src/admin/pages/FlowDiagram.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import '@xyflow/react/dist/style.css';

import FlowToolbar from '../components/workflows/FlowToolbar';
import FlowDiagramCanvas from '../components/workflows/FlowDiagramCanvas';
import EditorSidebar from '../components/workflows/EditorSidebar';
import LoadingView from '../../shared/components/LoadingView';
import AdminModal from '../components/common/AdminModal';
import NewWorkflowForm from '../components/workflows/NewWorkflowForm';

import useWorkflow from '../hooks/useWorkflow';
import useForms from '../hooks/useForms';
import useFlowGraphEditor from '../hooks/useFlowGraphEditor';
import useWorkflowEditorValidation from '../hooks/useWorkflowEditorValidation';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { fetchWithAuth } from '../auth/fetchWithAuth';

import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../config/env';

const NOTICE_TONE = {
  forked: 'info',
  declined: 'warning',
  error: 'danger',
  discard_failed: 'danger',
};
const NOTICE_IS_FAILURE = ['error', 'discard_failed'];

/**
 * Standing explanation of what a save did.
 *
 * Deliberately not a toast: the fork used to be reported, if at all, by a "Saved just now" that
 * cleared itself after three seconds while the id in the URL changed underneath. This stays
 * until it is dismissed, the draft is published, or a later save succeeds.
 */
function DraftForkBar({ notice, onDismiss }) {
  const forked = notice.kind === 'forked';

  return (
    <div
      className={`alert alert-${NOTICE_TONE[notice.kind] || 'warning'} alert-dismissible border-0 rounded-0 mb-0 py-2 small`}
      role={NOTICE_IS_FAILURE.includes(notice.kind) ? 'alert' : 'status'}
    >
      {notice.kind === 'discard_failed' ? (
        <>
          <strong className="me-2">Draft not discarded</strong>
          {notice.messages.join(' · ')}
          {' '}
          The draft is still here and nothing was deleted.
        </>
      ) : notice.kind === 'error' ? (
        <>
          <strong className="me-2">Not saved</strong>
          {notice.messages.join(' · ')}
        </>
      ) : forked ? (
        <>
          <strong className="me-2">Editing a new draft</strong>
          Your changes were saved to a draft. Workflow
          {' '}
          <a
            href={`/admin/flow/${notice.publishedId}/preview`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`#${notice.publishedId}`}
          </a>
          {' '}
          is still the live version, and stays that way until you publish this draft.
        </>
      ) : (
        <>
          <strong className="me-2">Not saved</strong>
          No draft was created, so the published version is untouched. Your changes are still on
          screen and will be saved to a draft next time — nothing has been written yet.
        </>
      )}
      <button type="button" className="btn-close" aria-label="Dismiss" onClick={onDismiss} />
    </div>
  );
}

export default function FlowDiagram() {
  const navigate = useNavigate();
  const { flow_id } = useParams();
  const { user, updateCurrentUser } = useCurrentUser();
  const { forms, loading: formsLoading } = useForms();

  const {
    workflow,
    setWorkflow,
    saveStatus,
    draftNotice,
    dismissDraftNotice,
    createNewWorkflow,
    loadWorkflowById,
    saveWorkflow,
    downloadWorkflow,
    publishWorkflow,
    validateWorkflow,
    discardDraft,
    loading,
  } = useWorkflow();

  const [selectedItem, setSelectedItem] = useState(null);
  const lastClickedNodeRef = useRef(null);
  const editorSidebarRef = useRef(null);

  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [isCreatingNewWorkflow, setIsCreatingNewWorkflow] = useState(false);
  const [showDetailedContent, setShowDetailedContent] = useState(false);
  const [selectedAddNodeType, setSelectedAddNodeType] = useState('html');

  const graph = useFlowGraphEditor({
    workflow,
    setWorkflow,
    saveWorkflow,
    showDetailedContent,
    selectedAddNodeType,
    selectedItem,
    setSelectedItem,
    lastClickedNodeRef,
    editorSidebarRef,
  });

  const {
    stepValidationMap,
    edgeValidationMap,
    warningItemsForToolbar,
    warningItemsForBar,
    showPublishErrorBar,
    publishBlocked,
    handlePublish,
    focusValidationIssue,
  } = useWorkflowEditorValidation({
    workflow,
    forms,
    formsLoading,
    validateWorkflow,
    publishWorkflow,
    setNodes: graph.setNodes,
    setEdges: graph.setEdges,
    fitView: graph.fitView,
    setSelectedItem,
    lastClickedNodeRef,
  });

  const clearLastClickedNode = useCallback(() => {
    lastClickedNodeRef.current = null;
  }, []);

  useEffect(() => {
    setWorkflow(null);
    if (flow_id) {
      loadWorkflowById(flow_id, graph.setNodes, graph.setEdges);
    }
  }, [flow_id, loadWorkflowById, graph.setNodes, graph.setEdges, setWorkflow]);

  useEffect(() => {
    if (flow_id === undefined) {
      setIsCreatingNewWorkflow(true);
      setShowWorkflowModal(true);
    }
  }, [flow_id]);

  const handlePreview = useCallback((previewFlowId) => {
    const id = previewFlowId ?? flow_id;
    if (!id) return;
    window.open(`/admin/flow/${id}/preview`, '_blank', 'noopener,noreferrer');
  }, [flow_id]);

  const handleCreateWorkflow = async (newWorkflow) => {
    try {
      const savedWorkflow = await createNewWorkflow(graph.setNodes, graph.setEdges, newWorkflow);
      setWorkflow(savedWorkflow);
      setShowWorkflowModal(false);
      setIsCreatingNewWorkflow(false);

      const isFirstUser = user?.first_user;
      const onboardingPrefs = user?.preferences?.onboarding || {};
      const isOnboardingActive = !onboardingPrefs.completed_at && !onboardingPrefs.dismissed_at;

      if (isFirstUser && isOnboardingActive && !onboardingPrefs.last_workflow_created_at) {
        try {
          const res = await fetchWithAuth(
            `${API_URL}/v1/admin/users/me/preferences`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                preferences: {
                  onboarding: { last_workflow_created_at: new Date().toISOString() },
                },
              }),
            }
          );
          if (res.ok) {
            const updatedUser = await res.json();
            updateCurrentUser(updatedUser);
          }
        } catch (trackingError) {
          console.error('Failed to track workflow creation:', trackingError);
        }
      }

      navigate(`/admin/flow/${savedWorkflow.id}/edit`);
    } catch (error) {
      console.error('Failed to create workflow:', error);
    }
  };

  const handleEditWorkflow = () => {
    setIsCreatingNewWorkflow(false);
    setShowWorkflowModal(true);
    setSelectedItem(null);
    clearLastClickedNode();
  };

  const handleUpdateWorkflow = async (workflowData) => {
    try {
      const updatedWorkflow = { ...workflow, ...workflowData };
      setWorkflow(updatedWorkflow);
      await saveWorkflow(updatedWorkflow);
      setShowWorkflowModal(false);
      setIsCreatingNewWorkflow(false);
    } catch (error) {
      console.error('Failed to update workflow:', error);
    }
  };

  if (loading) {
    return (
      <LoadingView
        symbolClass="bi bi-diagram-3"
        title="Loading Workflow"
        subtitle="Please wait while we load your workflow..."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', overflow: 'hidden' }}>
      <FlowToolbar
        onNewWorkflow={() => {
          if (workflow) {
            setIsCreatingNewWorkflow(true);
            setShowWorkflowModal(true);
          } else if (flow_id !== undefined) {
            navigate('/admin/flow/new');
          }
        }}
        onOpenWorkflow={() => {
          navigate('/admin/flows/');
        }}
        onSaveWorkflow={saveWorkflow}
        onDownloadJSON={downloadWorkflow}
        onUploadJSON={graph.handleUploadJSON}
        selectedAddNodeType={selectedAddNodeType}
        onSelectAddNodeType={setSelectedAddNodeType}
        setNodes={graph.setNodes}
        setEdges={graph.setEdges}
        onPreview={() => handlePreview(flow_id)}
        onPublish={handlePublish}
        onDiscardDraft={discardDraft}
        onEditWorkflow={handleEditWorkflow}
        saveStatus={saveStatus}
        workflowName={workflow?.name || ''}
        workflowState={workflow?.state}
        workflowVersion={workflow?.version}
        replaces={workflow?.replaces}
        pendingDraft={workflow?.pending_draft}
        showDetailedContent={showDetailedContent}
        onToggleDetailedContent={setShowDetailedContent}
        warningItems={warningItemsForToolbar}
        onValidationIssueClick={focusValidationIssue}
        publishDisabled={publishBlocked}
        onAutoLayout={graph.handleAutoLayout}
        autoLayoutDisabled={!workflow?.workflow_steps?.length}
      />

      {draftNotice && (
        <DraftForkBar notice={draftNotice} onDismiss={dismissDraftNotice} />
      )}

      {showPublishErrorBar && warningItemsForBar.length > 0 && (
        <div className="alert alert-warning border-0 rounded-0 mb-0 py-2 small" role="status">
          <strong className="me-2">Cannot publish yet</strong>
          Fix these issues, then try publishing again.
          <ul className="mb-0 mt-2 ps-3">
            {warningItemsForBar.map((w, i) => (
              <li key={`${i}-${w.message}`}>
                {w.violation ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-start text-warning-emphasis text-decoration-none"
                    onClick={() => focusValidationIssue(w)}
                  >
                    {w.message}
                  </button>
                ) : (
                  w.message
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ flex: 1, position: 'relative', display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        <FlowDiagramCanvas
          nodes={graph.nodes}
          edges={graph.edges}
          onNodesChange={graph.onNodesChange}
          onEdgesChange={graph.handleEdgesChange}
          onConnect={graph.handleConnect}
          onConnectEnd={graph.handleConnectEnd}
          onNodeClick={graph.onNodeClick}
          onEdgeClick={graph.onEdgeClick}
          onPaneClick={graph.onPaneClick}
          onNodesDelete={graph.onNodesDelete}
          onEdgesDelete={graph.onEdgesDelete}
          onNodeDrag={graph.onNodeDrag}
          onNodeDragStop={graph.onNodeDragStop}
          onDragOver={graph.handleDragOver}
          onDrop={graph.handleDrop}
          defaultEdgeOptions={graph.defaultEdgeOptions}
          nodeTypes={graph.nodeTypes}
          edgeTypes={graph.edgeTypes}
        />

        <EditorSidebar
          ref={editorSidebarRef}
          workflow={workflow}
          onWorkflowChange={graph.handleWorkflowChange}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          clearLastClickedNode={clearLastClickedNode}
          saveWorkflow={saveWorkflow}
          onAiStepsApplied={graph.handleAiStepsApplied}
          stepValidationMap={stepValidationMap}
          edgeValidationMap={edgeValidationMap}
        />
      </div>

      <AdminModal
        show={showWorkflowModal}
        onClose={() => {
          setShowWorkflowModal(false);
          if (flow_id === undefined) {
            navigate('/admin/flows/');
          }
        }}
        title={isCreatingNewWorkflow ? 'New Workflow' : 'Edit Workflow Settings'}
        size="lg"
      >
        <NewWorkflowForm
          workflow={isCreatingNewWorkflow ? null : workflow}
          onCreateWorkflow={handleCreateWorkflow}
          onUpdateWorkflow={handleUpdateWorkflow}
          onCancel={() => {
            setShowWorkflowModal(false);
            if (flow_id === undefined) {
              navigate('/admin/flows/');
            }
          }}
        />
      </AdminModal>
    </div>
  );
}
